import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  useMediaQuery
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { scheduleResourceAutocompletePlaceholderKey } from "../utils/resource-booking-schedule.utils";
import { BookableBookingObjectDto, ResourceKind } from "../typings/resource-calendar.types";
import {
  SCHEDULE_RESOURCE_NOT_PERMITTED_CHIP_ATTR,
  ScheduleResourceNotPermittedChip,
  ScheduleResourceNotPermittedDialog
} from "./ScheduleResourceNotPermittedDialog.partial";

export type ScheduleResourceAutocompleteOption = BookableBookingObjectDto & {
  searchLabel: string;
};

export { scheduleResourceAutocompletePlaceholderKey } from "../utils/resource-booking-schedule.utils";

export function isEventFromNotPermittedChip(event: SyntheticEvent): boolean {
  return event.target instanceof Element
    ? event.target.closest(`[${SCHEDULE_RESOURCE_NOT_PERMITTED_CHIP_ATTR}]`) != null
    : false;
}

type P = {
  isLoading: boolean;
  loadError: boolean;
  onResourceChange: (value: ScheduleResourceAutocompleteOption | null) => void;
  options: ScheduleResourceAutocompleteOption[];
  value: ScheduleResourceAutocompleteOption | null;
  resourceBookingType?: BookingType | null;
  resourceKind?: ResourceKind;
};

export function ScheduleResourceAutocomplete({
  isLoading,
  loadError,
  onResourceChange,
  options,
  value,
  resourceBookingType,
  resourceKind
}: Readonly<P>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const hasSelectedResource = value != null;
  const [emphasizeLoaded, setEmphasizeLoaded] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [notPermittedDialogOption, setNotPermittedDialogOption] =
    useState<ScheduleResourceAutocompleteOption | null>(null);
  const wasLoadingRef = useRef(isLoading);

  const isNotPermittedDialogFor = (option: ScheduleResourceAutocompleteOption): boolean =>
    notPermittedDialogOption?.bookingInventoryId === option.bookingInventoryId &&
    notPermittedDialogOption.bookingType === option.bookingType;

  const openNotPermittedDialog = (option: ScheduleResourceAutocompleteOption): void => {
    setIsAutocompleteOpen(false);
    setNotPermittedDialogOption(option);
  };

  useEffect(() => {
    const prevLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoading;
    if (prevLoading && !isLoading && options.length > 0) {
      setEmphasizeLoaded(true);
      const id = globalThis.setTimeout(() => setEmphasizeLoaded(false), 900);
      return () => globalThis.clearTimeout(id);
    }
    return undefined;
  }, [isLoading, options.length]);

  return (
    <>
      <Autocomplete
        multiple
        filterSelectedOptions
        getOptionLabel={option => option.searchLabel}
        isOptionEqualToValue={(option, selected) =>
          option.bookingInventoryId === selected.bookingInventoryId &&
          option.bookingType === selected.bookingType
        }
        loading={isLoading}
        disabled={isLoading}
        options={options}
        open={isAutocompleteOpen}
        onOpen={() => setIsAutocompleteOpen(true)}
        onClose={() => setIsAutocompleteOpen(false)}
        value={value ? [value] : []}
        onChange={(_, selected) => {
          if (selected.length === 0) {
            onResourceChange(null);
            return;
          }
          // `multiple` is only for chip UI; keep a single resource (latest pick wins if list ever returns >1).
          onResourceChange(selected[selected.length - 1]);
        }}
        sx={{
          flex: "1 1 280px",
          minWidth: { xs: "100%", sm: 280 },
          maxWidth: "100%",
          // animate the loading wave
          "@keyframes scheduleResourceAutocompleteWave": {
            "0%": { transform: "translate(-100%, -50%)" },
            "100%": { transform: "translate(250%, -50%)" }
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 1.5,
            ...(emphasizeLoaded && {
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: "calc(50% + 2px)",
                height: "calc(100% - 4px)",
                width: "45%",
                background: `linear-gradient(
                90deg,
                transparent 0%,
                ${alpha(theme.palette.primary.main, 0.04)} 15%,
                ${alpha(theme.palette.primary.main, 0.24)} 50%,
                ${alpha(theme.palette.primary.main, 0.04)} 85%,
                transparent 100%
              )`,
                animation:
                  "scheduleResourceAutocompleteWave 2.2s cubic-bezier(0.33, 0, 0.2, 1) forwards",
                pointerEvents: "none",
                borderRadius: "inherit"
              }
            })
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            // variant="outlined"
            fullWidth
            error={loadError}
            helperText={loadError ? t("_scheduleResourceCatalogLoadError") : undefined}
            placeholder={
              hasSelectedResource
                ? undefined
                : t(scheduleResourceAutocompletePlaceholderKey(resourceBookingType, resourceKind))
            }
            slotProps={{
              input: {
                ...params.InputProps,
                sx: { height: "50px" },
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
        noOptionsText={t("No Options")}
        renderOption={(props, option) => {
          const { key, onClick, onMouseDown, ...optionProps } = props;
          return (
            <li
              {...optionProps}
              key={key ?? `${option.bookingType}-${option.bookingInventoryId}`}
              onClick={event => {
                if (isEventFromNotPermittedChip(event)) {
                  return;
                }
                onClick?.(event);
              }}
              onMouseDown={event => {
                if (isEventFromNotPermittedChip(event)) {
                  event.preventDefault();
                  event.stopPropagation();
                  return;
                }
                onMouseDown?.(event);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  width: "100%",
                  minWidth: 0
                }}
              >
                <Typography noWrap sx={{ minWidth: 0, flex: "0 1 auto" }}>
                  {option.searchLabel}
                </Typography>
                {option.isPermitted === false ? (
                  <Box sx={{ flexShrink: 0 }}>
                    <ScheduleResourceNotPermittedChip
                      expanded={isNotPermittedDialogFor(option)}
                      onOpen={() => openNotPermittedDialog(option)}
                    />
                  </Box>
                ) : null}
              </Box>
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const tagLabel = isMobile ? option.objectName : option.searchLabel;
            if (option.isPermitted !== false) {
              return (
                <Chip
                  {...tagProps}
                  key={key}
                  data-testid={`${option.bookingInventoryId}-select-chip`}
                  label={tagLabel}
                />
              );
            }

            return (
              <Chip
                {...tagProps}
                key={key}
                data-testid={`${option.bookingInventoryId}-select-chip`}
                sx={{
                  "& .MuiChip-label": {
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    overflow: "hidden"
                  }
                }}
                label={
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
                  >
                    <Typography component="span" noWrap variant="inherit">
                      {tagLabel}
                    </Typography>
                    <ScheduleResourceNotPermittedChip
                      expanded={isNotPermittedDialogFor(option)}
                      onOpen={() => openNotPermittedDialog(option)}
                    />
                  </Box>
                }
              />
            );
          })
        }
      />
      <ScheduleResourceNotPermittedDialog
        open={notPermittedDialogOption != null}
        onClose={() => setNotPermittedDialogOption(null)}
        assignees={notPermittedDialogOption?.permissionAssignees}
        objectName={notPermittedDialogOption?.objectName ?? ""}
      />
    </>
  );
}
